/**
 * Hala Control Center - Production-Grade Catalog Management
 * Windows-level UX clarity - Self-explanatory system
 * Arabic First (RTL) - No training needed
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus, Search, Edit2, Trash2, Eye, Power, ChevronRight, ChevronDown,
  Folder, Tag, FileText, MapPin, AlertTriangle, Info, CheckCircle, X,
  Building, Globe, User, Layers, Zap, TrendingUp, MessageCircle, Store,
  BookOpen, Compass, ArrowRight, ArrowLeft, Monitor, Users, Calendar,
  Link2, Unlink, ExternalLink, HelpCircle, Check, Upload, FileSpreadsheet,
  FileJson, Table2, AlertCircle, RotateCcw, ShieldAlert, RefreshCw, Download,
  CircleCheck, CircleX, Loader2
} from 'lucide-react';
import ExcelImportModal from '../../components/admin/ExcelImportModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/catalog`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('hala_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const COLORS = {
  primary: '#1B3A6B',
  primaryLight: '#2A4A7A',
  gold: '#C5A572',
  success: '#059669',
  warning: '#D97706',
  error: '#DC2626',
  info: '#2563EB',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
  textLight: '#94A3B8',
};

const CATEGORY_TYPES = [
  { value: 'training', label: 'تدريب', icon: BookOpen, color: '#2563EB' },
  { value: 'consultation', label: 'استشارة', icon: MessageCircle, color: '#059669' },
  { value: 'workspace', label: 'مساحة عمل', icon: Building, color: '#7C3AED' },
  { value: 'events', label: 'فعالية', icon: Calendar, color: '#D97706' },
  { value: 'digital', label: 'خدمة رقمية', icon: Monitor, color: '#0891B2' },
  { value: 'other', label: 'أخرى', icon: Layers, color: '#64748B' },
];

const DELIVERY_OPTIONS = [
  { value: 'branch', label: 'فرع', icon: Building, color: '#059669', description: 'تقديم الخدمة في أحد الفروع' },
  { value: 'online', label: 'أونلاين', icon: Globe, color: '#2563EB', description: 'تقديم الخدمة عن بُعد' },
  { value: 'consultant', label: 'مستشار', icon: User, color: '#7C3AED', description: 'من خلال مستشار متخصص' },
  { value: 'hybrid', label: 'هجين', icon: Layers, color: '#D97706', description: 'مزيج من الحضوري والإلكتروني' },
];

const ICONS = [
  { value: 'folder', icon: Folder, label: 'مجلد' },
  { value: 'compass', icon: Compass, label: 'بوصلة' },
  { value: 'zap', icon: Zap, label: 'برق' },
  { value: 'trending-up', icon: TrendingUp, label: 'نمو' },
  { value: 'book-open', icon: BookOpen, label: 'كتاب' },
  { value: 'users', icon: Users, label: 'مستخدمين' },
  { value: 'building', icon: Building, label: 'مبنى' },
  { value: 'store', icon: Store, label: 'متجر' },
];

// Resource type labels for display
const RESOURCE_TYPE_LABELS = {
  'consultant': 'مستشار',
  'training_program': 'برنامج تدريبي',
  'event': 'فعالية',
  'workspace_package': 'مساحة عمل',
  'digital_service': 'خدمة رقمية',
  'branch_service': 'خدمة فرع',
};

// ═══════════════════════════════════════════════════════════════════════════════
// REUSABLE COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

// Alert Component
function Alert({ type = 'info', children, onClose, style = {} }) {
  const configs = {
    error: { bg: '#FEF2F2', border: '#FECACA', color: '#991B1B', icon: AlertTriangle, label: 'خطأ' },
    warning: { bg: '#FFFBEB', border: '#FDE68A', color: '#92400E', icon: AlertTriangle, label: 'تنبيه' },
    info: { bg: '#EFF6FF', border: '#BFDBFE', color: '#1E40AF', icon: Info, label: 'معلومة' },
    success: { bg: '#F0FDF4', border: '#BBF7D0', color: '#166534', icon: CheckCircle, label: 'نجاح' },
  };
  const config = configs[type] || configs.info;
  const Icon = config.icon;
  
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '12px',
      padding: '14px 16px', backgroundColor: config.bg,
      border: `1px solid ${config.border}`, borderRadius: '10px',
      ...style
    }}>
      <div style={{
        width: '24px', height: '24px', borderRadius: '50%',
        backgroundColor: `${config.color}20`, display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        <Icon size={14} color={config.color} />
      </div>
      <div style={{ flex: 1, fontSize: '14px', color: config.color, lineHeight: 1.6 }}>
        {children}
      </div>
      {onClose && (
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <X size={16} color={config.color} />
        </button>
      )}
    </div>
  );
}

// Form Field with Label
function FormField({ label, required, hint, error, children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <label style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        fontSize: '14px', fontWeight: 600, color: COLORS.text, marginBottom: '8px'
      }}>
        {label}
        {required && <span style={{ color: COLORS.error }}>*</span>}
        {hint && (
          <span style={{ 
            fontSize: '12px', fontWeight: 400, color: COLORS.textMuted,
            marginRight: '8px'
          }}>({hint})</span>
        )}
      </label>
      {children}
      {error && (
        <p style={{ margin: '6px 0 0', fontSize: '12px', color: COLORS.error, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <AlertTriangle size={12} /> {error}
        </p>
      )}
    </div>
  );
}

// Input Component
function Input({ value, onChange, placeholder, disabled, error, ...props }) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        width: '100%', padding: '12px 14px', borderRadius: '10px',
        border: `1.5px solid ${error ? COLORS.error : COLORS.border}`,
        fontSize: '14px', fontFamily: 'inherit', outline: 'none',
        backgroundColor: disabled ? COLORS.bgDark : COLORS.white,
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
      onFocus={(e) => { e.target.style.borderColor = COLORS.primary; e.target.style.boxShadow = `0 0 0 3px ${COLORS.primary}20`; }}
      onBlur={(e) => { e.target.style.borderColor = error ? COLORS.error : COLORS.border; e.target.style.boxShadow = 'none'; }}
      {...props}
    />
  );
}

// Select Component
function Select({ value, onChange, options, placeholder, disabled, error }) {
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      style={{
        width: '100%', padding: '12px 14px', borderRadius: '10px',
        border: `1.5px solid ${error ? COLORS.error : COLORS.border}`,
        fontSize: '14px', fontFamily: 'inherit', outline: 'none',
        backgroundColor: disabled ? COLORS.bgDark : COLORS.white,
        cursor: disabled ? 'not-allowed' : 'pointer',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748B'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left 12px center',
        backgroundSize: '16px',
        paddingLeft: '36px',
      }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
}

// Textarea Component
function Textarea({ value, onChange, placeholder, rows = 3, ...props }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%', padding: '12px 14px', borderRadius: '10px',
        border: `1.5px solid ${COLORS.border}`, fontSize: '14px',
        fontFamily: 'inherit', outline: 'none', resize: 'vertical',
        minHeight: '80px',
      }}
      onFocus={(e) => { e.target.style.borderColor = COLORS.primary; }}
      onBlur={(e) => { e.target.style.borderColor = COLORS.border; }}
      {...props}
    />
  );
}

// Checkbox Card
function CheckboxCard({ checked, onChange, icon: Icon, label, description, color }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: '14px',
      padding: '16px', borderRadius: '12px', cursor: 'pointer',
      border: `2px solid ${checked ? color : COLORS.border}`,
      backgroundColor: checked ? `${color}08` : COLORS.white,
      transition: 'all 0.2s',
    }}>
      <div style={{
        width: '22px', height: '22px', borderRadius: '6px',
        border: `2px solid ${checked ? color : COLORS.border}`,
        backgroundColor: checked ? color : COLORS.white,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, marginTop: '2px',
      }}>
        {checked && <Check size={14} color={COLORS.white} />}
      </div>
      <input type="checkbox" checked={checked} onChange={onChange} style={{ display: 'none' }} />
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <Icon size={18} color={color} />
          <span style={{ fontSize: '15px', fontWeight: 600, color: COLORS.text }}>{label}</span>
        </div>
        <p style={{ margin: 0, fontSize: '13px', color: COLORS.textMuted, lineHeight: 1.5 }}>{description}</p>
      </div>
    </label>
  );
}

// Badge Component
function Badge({ children, color = COLORS.primary, icon: Icon }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '4px 10px', borderRadius: '6px',
      backgroundColor: `${color}15`, color: color,
      fontSize: '12px', fontWeight: 500,
    }}>
      {Icon && <Icon size={12} />}
      {children}
    </span>
  );
}

// Empty State
function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div style={{
      padding: '60px 40px', textAlign: 'center',
      backgroundColor: COLORS.bg, borderRadius: '12px',
      border: `2px dashed ${COLORS.border}`,
    }}>
      <div style={{
        width: '64px', height: '64px', borderRadius: '50%',
        backgroundColor: COLORS.white, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 20px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
      }}>
        <Icon size={28} color={COLORS.textMuted} />
      </div>
      <h3 style={{ fontSize: '16px', fontWeight: 600, color: COLORS.text, margin: '0 0 8px' }}>{title}</h3>
      <p style={{ fontSize: '14px', color: COLORS.textMuted, margin: '0 0 20px', maxWidth: '300px', marginLeft: 'auto', marginRight: 'auto' }}>{description}</p>
      {action}
    </div>
  );
}

// Button Component
function Button({ children, variant = 'primary', size = 'md', icon: Icon, onClick, disabled, loading, ...props }) {
  const variants = {
    primary: { bg: COLORS.primary, color: COLORS.white, border: 'none' },
    success: { bg: COLORS.success, color: COLORS.white, border: 'none' },
    danger: { bg: COLORS.error, color: COLORS.white, border: 'none' },
    warning: { bg: COLORS.warning, color: COLORS.white, border: 'none' },
    outline: { bg: 'transparent', color: COLORS.text, border: `1.5px solid ${COLORS.border}` },
    ghost: { bg: 'transparent', color: COLORS.textMuted, border: 'none' },
  };
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px' },
    md: { padding: '12px 20px', fontSize: '14px' },
    lg: { padding: '14px 28px', fontSize: '15px' },
  };
  const v = variants[variant];
  const s = sizes[size];
  
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        padding: s.padding, fontSize: s.fontSize, fontWeight: 600,
        fontFamily: 'inherit', borderRadius: '10px', cursor: disabled ? 'not-allowed' : 'pointer',
        backgroundColor: disabled ? COLORS.bgDark : v.bg,
        color: disabled ? COLORS.textLight : v.color,
        border: v.border, transition: 'all 0.2s',
        opacity: disabled ? 0.6 : 1,
      }}
      {...props}
    >
      {loading ? (
        <div style={{ width: '16px', height: '16px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      ) : Icon ? <Icon size={16} /> : null}
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROGRAM CARD
// ═══════════════════════════════════════════════════════════════════════════════

function ProgramCard({ program, isSelected, onClick, onEdit, onToggle, onDelete }) {
  const IconComp = ICONS.find(i => i.value === program.icon)?.icon || Folder;
  
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: isSelected ? `${COLORS.primary}06` : COLORS.white,
        border: `2px solid ${isSelected ? COLORS.primary : COLORS.border}`,
        borderRadius: '16px', padding: '20px', cursor: 'pointer',
        transition: 'all 0.2s', position: 'relative',
      }}
    >
      {/* Info Badge */}
      <div style={{ position: 'absolute', top: '12px', left: '12px' }}>
        <Badge color={COLORS.info} icon={Info}>للتنظيم فقط</Badge>
      </div>
      
      {/* Status Badge */}
      {!program.is_active && (
        <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
          <Badge color={COLORS.error}>غير نشط</Badge>
        </div>
      )}
      
      {/* Icon and Name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '24px', marginBottom: '16px' }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '14px',
          backgroundColor: program.color || COLORS.primary,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          <IconComp size={28} color={COLORS.white} />
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: COLORS.text }}>{program.name_ar}</h3>
          {program.name_en && <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>{program.name_en}</p>}
        </div>
      </div>
      
      {/* Stats */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '16px', padding: '12px 0', borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}` }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: COLORS.primary }}>{program.categories_count || 0}</p>
          <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textMuted }}>تصنيف</p>
        </div>
        <div style={{ width: '1px', backgroundColor: COLORS.border }} />
        <div style={{ flex: 1, textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: COLORS.success }}>{program.services_count || 0}</p>
          <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.textMuted }}>خدمة</p>
        </div>
      </div>
      
      {/* Actions */}
      <div style={{ display: 'flex', gap: '8px' }} onClick={e => e.stopPropagation()}>
        <Button variant="outline" size="sm" icon={Edit2} onClick={onEdit} style={{ flex: 1 }}>تعديل</Button>
        <Button variant="ghost" size="sm" icon={Power} onClick={onToggle} style={{ color: program.is_active ? COLORS.warning : COLORS.success }} />
        <Button variant="ghost" size="sm" icon={Trash2} onClick={onDelete} style={{ color: COLORS.error }} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// EDIT PROGRAM MODAL
// ═══════════════════════════════════════════════════════════════════════════════

function EditProgramModal({ program, isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name_ar: '', name_en: '', description_ar: '', icon: 'folder', color: '#1B3A6B', is_active: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (program) {
      setFormData({
        name_ar: program.name_ar || '',
        name_en: program.name_en || '',
        description_ar: program.description_ar || '',
        icon: program.icon || 'folder',
        color: program.color || '#1B3A6B',
        is_active: program.is_active !== false,
      });
    } else {
      setFormData({ name_ar: '', name_en: '', description_ar: '', icon: 'folder', color: '#1B3A6B', is_active: true });
    }
    setErrors({});
  }, [program, isOpen]);
  
  const validate = () => {
    const errs = {};
    if (!formData.name_ar.trim()) errs.name_ar = 'اسم البرنامج بالعربية مطلوب';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (e) {
      setErrors({ submit: e.response?.data?.detail || 'حدث خطأ' });
    }
    setLoading(false);
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '520px', maxWidth: '95vw', maxHeight: '90vh',
        backgroundColor: COLORS.white, borderRadius: '20px',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)', zIndex: 1001,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '24px', borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: COLORS.text }}>
                {program ? 'تعديل البرنامج' : 'إنشاء برنامج جديد'}
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: '14px', color: COLORS.textMuted }}>
                البرنامج يُستخدم للتنظيم فقط ولا يؤثر على توصيات الذكاء الاصطناعي
              </p>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}>
              <X size={24} color={COLORS.textMuted} />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {errors.submit && <Alert type="error" style={{ marginBottom: '20px' }}>{errors.submit}</Alert>}
          
          <FormField label="اسم البرنامج بالعربية" required error={errors.name_ar}>
            <Input
              value={formData.name_ar}
              onChange={e => setFormData({ ...formData, name_ar: e.target.value })}
              placeholder="مثال: برنامج دلني"
              error={errors.name_ar}
            />
          </FormField>
          
          <FormField label="اسم البرنامج بالإنجليزية" hint="اختياري">
            <Input
              value={formData.name_en}
              onChange={e => setFormData({ ...formData, name_en: e.target.value })}
              placeholder="Example: Dallni Program"
            />
          </FormField>
          
          <FormField label="وصف مختصر" hint="اختياري">
            <Textarea
              value={formData.description_ar}
              onChange={e => setFormData({ ...formData, description_ar: e.target.value })}
              placeholder="وصف موجز للبرنامج..."
            />
          </FormField>
          
          <FormField label="الأيقونة">
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {ICONS.map(ic => {
                const IconC = ic.icon;
                return (
                  <button
                    key={ic.value}
                    onClick={() => setFormData({ ...formData, icon: ic.value })}
                    style={{
                      width: '48px', height: '48px', borderRadius: '12px',
                      border: `2px solid ${formData.icon === ic.value ? COLORS.primary : COLORS.border}`,
                      backgroundColor: formData.icon === ic.value ? `${COLORS.primary}10` : COLORS.white,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <IconC size={22} color={formData.icon === ic.value ? COLORS.primary : COLORS.textMuted} />
                  </button>
                );
              })}
            </div>
          </FormField>
          
          <FormField label="الحالة">
            <div style={{ display: 'flex', gap: '12px' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 20px',
                borderRadius: '10px', cursor: 'pointer',
                border: `2px solid ${formData.is_active ? COLORS.success : COLORS.border}`,
                backgroundColor: formData.is_active ? `${COLORS.success}10` : COLORS.white,
              }}>
                <input type="radio" checked={formData.is_active} onChange={() => setFormData({ ...formData, is_active: true })} style={{ display: 'none' }} />
                <CheckCircle size={18} color={formData.is_active ? COLORS.success : COLORS.textMuted} />
                <span style={{ fontSize: '14px', fontWeight: 500, color: formData.is_active ? COLORS.success : COLORS.textMuted }}>نشط</span>
              </label>
              <label style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 20px',
                borderRadius: '10px', cursor: 'pointer',
                border: `2px solid ${!formData.is_active ? COLORS.error : COLORS.border}`,
                backgroundColor: !formData.is_active ? `${COLORS.error}10` : COLORS.white,
              }}>
                <input type="radio" checked={!formData.is_active} onChange={() => setFormData({ ...formData, is_active: false })} style={{ display: 'none' }} />
                <Power size={18} color={!formData.is_active ? COLORS.error : COLORS.textMuted} />
                <span style={{ fontSize: '14px', fontWeight: 500, color: !formData.is_active ? COLORS.error : COLORS.textMuted }}>غير نشط</span>
              </label>
            </div>
          </FormField>
        </div>
        
        {/* Footer */}
        <div style={{ padding: '20px 24px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button variant="success" icon={Check} onClick={handleSubmit} loading={loading}>
            {program ? 'حفظ التعديلات' : 'إنشاء البرنامج'}
          </Button>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); }}`}</style>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SERVICE TABLE
// ═══════════════════════════════════════════════════════════════════════════════

function ServiceTable({ services, loading, onEdit, onToggle, onDelete, onView }) {
  if (loading) {
    return (
      <div style={{ padding: '80px', textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: `3px solid ${COLORS.border}`, borderTopColor: COLORS.primary, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>جاري تحميل الخدمات...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); }}`}</style>
      </div>
    );
  }
  
  if (!services || services.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="لا توجد خدمات"
        description="لم يتم إضافة أي خدمات بعد. استخدم زر إضافة خدمة لإنشاء خدمة جديدة."
      />
    );
  }
  
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: COLORS.bg }}>
            <th style={{ padding: '16px 20px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>الخدمة</th>
            <th style={{ padding: '16px 20px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>التصنيف</th>
            <th style={{ padding: '16px 20px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>طريقة التقديم</th>
            <th style={{ padding: '16px 20px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>الحالة</th>
            <th style={{ padding: '16px 20px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                AI
                <HelpCircle size={14} color={COLORS.textMuted} />
              </div>
            </th>
            <th style={{ padding: '16px 20px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: COLORS.textMuted }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                حالة العرض
                <HelpCircle size={14} color={COLORS.textMuted} title="هل الخدمة تحتوي على عناصر عرض (مستشارين، برامج، فعاليات)" />
              </div>
            </th>
            <th style={{ padding: '16px 20px', width: '160px' }}></th>
          </tr>
        </thead>
        <tbody>
          {services.map(svc => (
            <tr key={svc.service_id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
              <td style={{ padding: '18px 20px' }}>
                <div>
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: COLORS.text }}>{svc.name_ar}</p>
                  {svc.name_en && <p style={{ margin: '4px 0 0', fontSize: '12px', color: COLORS.textMuted }}>{svc.name_en}</p>}
                </div>
              </td>
              <td style={{ padding: '18px 20px' }}>
                <Badge color={COLORS.gold} icon={Tag}>{svc.category_name_ar || '-'}</Badge>
              </td>
              <td style={{ padding: '18px 20px' }}>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {svc.delivery_methods?.length > 0 ? (
                    svc.delivery_methods.map((dm, i) => {
                      const opt = DELIVERY_OPTIONS.find(o => o.value === dm.delivery_type);
                      const Icon = opt?.icon || MapPin;
                      const cityDisplay = dm.city_name || dm.city || '';
                      return (
                        <Badge key={i} color={opt?.color || COLORS.textMuted} icon={Icon}>
                          {cityDisplay || opt?.label}
                        </Badge>
                      );
                    })
                  ) : (
                    <Badge color={COLORS.error} icon={AlertTriangle}>بدون توصيل</Badge>
                  )}
                </div>
              </td>
              <td style={{ padding: '18px 20px' }}>
                <Badge color={svc.is_active ? COLORS.success : COLORS.error} icon={svc.is_active ? CheckCircle : Power}>
                  {svc.is_active ? 'نشط' : 'غير نشط'}
                </Badge>
              </td>
              <td style={{ padding: '18px 20px' }}>
                {svc.is_ai_mapped ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Link2 size={16} color={COLORS.success} />
                    <span style={{ fontSize: '13px', color: COLORS.success, fontWeight: 500 }}>مرتبطة</span>
                  </div>
                ) : (
                  <div title="لن تظهر هذه الخدمة في نتائج هلا" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'help' }}>
                    <Unlink size={16} color={COLORS.warning} />
                    <span style={{ fontSize: '13px', color: COLORS.warning, fontWeight: 500 }}>غير مرتبطة</span>
                  </div>
                )}
              </td>
              <td style={{ padding: '18px 20px' }}>
                {svc.has_resources ? (
                  <div title={Object.entries(svc.resource_types || {}).map(([k, v]) => `${RESOURCE_TYPE_LABELS[k] || k}: ${v}`).join('\n')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <CheckCircle size={16} color={COLORS.success} />
                      <span style={{ fontSize: '13px', color: COLORS.success, fontWeight: 500 }}>مكتمل ({svc.resources_count})</span>
                    </div>
                    {svc.resource_types && Object.keys(svc.resource_types).length > 0 && (
                      <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                        {Object.entries(svc.resource_types).slice(0, 3).map(([type, count]) => (
                          <span key={type} style={{
                            fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
                            backgroundColor: `${COLORS.success}15`, color: COLORS.success,
                          }}>
                            {RESOURCE_TYPE_LABELS[type] || type} ({count})
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div title="هذه الخدمة لا تحتوي على عناصر عرض" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'help' }}>
                    <AlertTriangle size={16} color={COLORS.warning} />
                    <span style={{ fontSize: '13px', color: COLORS.warning, fontWeight: 500 }}>غير مكتمل</span>
                  </div>
                )}
              </td>
              <td style={{ padding: '18px 20px' }}>
                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                  <Button variant="ghost" size="sm" icon={Eye} onClick={() => onView(svc)} title="عرض التفاصيل" />
                  <Button variant="ghost" size="sm" icon={Edit2} onClick={() => onEdit(svc)} title="تعديل" />
                  <Button variant="ghost" size="sm" icon={Power} onClick={() => onToggle(svc)} style={{ color: svc.is_active ? COLORS.warning : COLORS.success }} title={svc.is_active ? 'تعطيل' : 'تفعيل'} />
                  <Button variant="ghost" size="sm" icon={Trash2} onClick={() => onDelete(svc)} style={{ color: COLORS.error }} title="حذف" />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADD CITY MODAL
// ═══════════════════════════════════════════════════════════════════════════════

function AddCityModal({ isOpen, onClose, onCityAdded }) {
  const [cityName, setCityName] = useState('');
  const [cityNameEn, setCityNameEn] = useState('');
  const [region, setRegion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setCityName('');
      setCityNameEn('');
      setRegion('');
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!cityName.trim()) {
      setError('اسم المدينة مطلوب');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/cities`, {
        name_ar: cityName.trim(),
        name_en: cityNameEn.trim(),
        region: region.trim()
      }, { headers: getAuthHeaders() });
      onCityAdded(res.data);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'حدث خطأ أثناء إضافة المدينة');
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100 }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '400px', maxWidth: '95vw',
        backgroundColor: COLORS.white, borderRadius: '16px',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)', zIndex: 1101,
        overflow: 'hidden',
      }}>
        <div style={{ padding: '20px', borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: COLORS.text }}>إضافة مدينة جديدة</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
              <X size={20} color={COLORS.textMuted} />
            </button>
          </div>
        </div>
        
        <div style={{ padding: '20px' }}>
          {error && <Alert type="error" style={{ marginBottom: '16px' }}>{error}</Alert>}
          
          <FormField label="اسم المدينة بالعربية" required>
            <Input
              value={cityName}
              onChange={e => setCityName(e.target.value)}
              placeholder="مثال: ينبع"
              autoFocus
            />
          </FormField>
          
          <FormField label="اسم المدينة بالإنجليزية" hint="اختياري">
            <Input
              value={cityNameEn}
              onChange={e => setCityNameEn(e.target.value)}
              placeholder="Example: Yanbu"
            />
          </FormField>
          
          <FormField label="المنطقة" hint="اختياري">
            <Input
              value={region}
              onChange={e => setRegion(e.target.value)}
              placeholder="مثال: المنطقة الغربية"
            />
          </FormField>
        </div>
        
        <div style={{ padding: '16px 20px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button variant="success" icon={Plus} onClick={handleSubmit} loading={loading}>إضافة المدينة</Button>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SUCCESS MODAL (After Service Creation)
// ═══════════════════════════════════════════════════════════════════════════════

function SuccessModal({ isOpen, onClose, onAddAnother, onViewServices, serviceName, isEdit }) {
  if (!isOpen) return null;

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '450px', maxWidth: '95vw',
        backgroundColor: COLORS.white, borderRadius: '20px',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)', zIndex: 1101,
        textAlign: 'center', padding: '40px 32px',
      }}>
        <div style={{
          width: '80px', height: '80px', borderRadius: '50%',
          backgroundColor: `${COLORS.success}15`, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px',
        }}>
          <CheckCircle size={40} color={COLORS.success} />
        </div>
        
        <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 700, color: COLORS.text }}>
          {isEdit ? 'تم تعديل الخدمة بنجاح' : 'تم إنشاء الخدمة بنجاح'}
        </h2>
        <p style={{ margin: '0 0 32px', fontSize: '15px', color: COLORS.textMuted, lineHeight: 1.6 }}>
          {isEdit 
            ? <>تم تحديث الخدمة "<span style={{ fontWeight: 600, color: COLORS.text }}>{serviceName}</span>" بنجاح</>
            : <>تمت إضافة الخدمة "<span style={{ fontWeight: 600, color: COLORS.text }}>{serviceName}</span>" إلى الكتالوج</>
          }
        </p>
        
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          {onAddAnother && (
            <Button variant="outline" icon={Plus} onClick={onAddAnother} size="lg">
              إضافة خدمة أخرى
            </Button>
          )}
          <Button variant="success" icon={Eye} onClick={onViewServices} size="lg">
            عرض الخدمات
          </Button>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MULTI-CITY SELECTOR
// ═══════════════════════════════════════════════════════════════════════════════

function MultiCitySelector({ selectedCities, onChange, cities, onAddCity }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleCity = (cityName) => {
    if (selectedCities.includes(cityName)) {
      onChange(selectedCities.filter(c => c !== cityName));
    } else {
      onChange([...selectedCities, cityName]);
    }
  };
  
  return (
    <div style={{ position: 'relative' }}>
      {/* Selected Cities Display */}
      <div 
        onClick={() => setIsOpen(!isOpen)}
        style={{
          minHeight: '48px', padding: '8px 12px', borderRadius: '10px',
          border: `1.5px solid ${COLORS.border}`, backgroundColor: COLORS.white,
          cursor: 'pointer', display: 'flex', flexWrap: 'wrap', gap: '8px',
          alignItems: 'center',
        }}
      >
        {selectedCities.length === 0 ? (
          <span style={{ fontSize: '14px', color: COLORS.textMuted }}>-- اختر المدن --</span>
        ) : (
          selectedCities.map(city => (
            <span key={city} style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '4px 10px', backgroundColor: `${COLORS.success}15`,
              borderRadius: '6px', fontSize: '13px', color: COLORS.success, fontWeight: 500,
            }}>
              <MapPin size={12} />
              {city}
              <button
                onClick={(e) => { e.stopPropagation(); toggleCity(city); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}
              >
                <X size={14} color={COLORS.success} />
              </button>
            </span>
          ))
        )}
        <ChevronDown size={18} color={COLORS.textMuted} style={{ marginRight: 'auto' }} />
      </div>
      
      {/* Dropdown */}
      {isOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 50 }} onClick={() => setIsOpen(false)} />
          <div style={{
            position: 'absolute', top: '100%', right: 0, left: 0,
            marginTop: '4px', backgroundColor: COLORS.white,
            borderRadius: '12px', border: `1px solid ${COLORS.border}`,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)', zIndex: 51,
            maxHeight: '280px', overflowY: 'auto',
          }}>
            {/* Add City Button */}
            <button
              onClick={(e) => { e.stopPropagation(); setIsOpen(false); onAddCity(); }}
              style={{
                width: '100%', padding: '12px 16px', display: 'flex',
                alignItems: 'center', gap: '10px', border: 'none',
                borderBottom: `1px solid ${COLORS.border}`,
                backgroundColor: `${COLORS.primary}08`, cursor: 'pointer',
                fontSize: '14px', fontWeight: 600, color: COLORS.primary,
              }}
            >
              <Plus size={18} />
              إضافة مدينة جديدة
            </button>
            
            {/* City Options */}
            {cities.map(city => {
              const isSelected = selectedCities.includes(city.name_ar);
              return (
                <button
                  key={city.city_id}
                  onClick={(e) => { e.stopPropagation(); toggleCity(city.name_ar); }}
                  style={{
                    width: '100%', padding: '12px 16px', display: 'flex',
                    alignItems: 'center', justifyContent: 'space-between',
                    border: 'none', backgroundColor: isSelected ? `${COLORS.success}10` : 'transparent',
                    cursor: 'pointer', fontSize: '14px', color: COLORS.text,
                    borderBottom: `1px solid ${COLORS.border}`,
                  }}
                >
                  <span>{city.name_ar}</span>
                  {isSelected && <Check size={18} color={COLORS.success} />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SERVICE WIZARD - PRODUCTION GRADE
// ═══════════════════════════════════════════════════════════════════════════════

function ServiceWizard({ isOpen, onClose, onComplete, programs, categories, contextProgramId, contextCategoryId, editingService }) {
  const isEditMode = !!editingService;
  const [step, setStep] = useState(isEditMode ? 3 : 1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Cities from API
  const [cities, setCities] = useState([]);
  const [showAddCityModal, setShowAddCityModal] = useState(false);
  const [pendingDeliveryType, setPendingDeliveryType] = useState(null);
  
  // Success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdServiceName, setCreatedServiceName] = useState('');
  
  // Step 1 - Program
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [createNewProgram, setCreateNewProgram] = useState(false);
  const [newProgram, setNewProgram] = useState({ name_ar: '', name_en: '' });
  
  // Step 2 - Category
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [createNewCategory, setCreateNewCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({ name_ar: '', name_en: '', category_type: 'training' });
  
  // Step 3 - Delivery
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  
  // Step 4 - Service Details
  const [serviceData, setServiceData] = useState({
    name_ar: '', name_en: '', short_description_ar: '', description_ar: '', duration: '', is_free: true
  });
  
  // Fetch cities on mount
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const res = await axios.get(`${API}/cities`, { headers: getAuthHeaders() });
        setCities(res.data);
      } catch (e) { console.error(e); }
    };
    if (isOpen) fetchCities();
  }, [isOpen]);
  
  // Initialize with context values
  useEffect(() => {
    if (isOpen && editingService) {
      // Edit mode - populate all fields
      setSelectedProgramId(editingService.program_id || '');
      const cat = categories.find(c => c.category_id === editingService.category_id);
      setSelectedCategoryId(editingService.category_id || '');
      if (cat) setSelectedProgramId(cat.program_id || '');
      
      // Group delivery methods by type with cities
      const dmMap = {};
      (editingService.delivery_methods || []).forEach(dm => {
        const type = dm.delivery_type;
        if (!dmMap[type]) {
          dmMap[type] = { delivery_type: type, cities: [], branch_name: dm.branch_name || '', meeting_link: dm.meeting_link || '', is_active: true };
        }
        const cityName = dm.city_name || dm.city || '';
        if (cityName && !dmMap[type].cities.includes(cityName)) {
          dmMap[type].cities.push(cityName);
        }
      });
      setDeliveryMethods(Object.values(dmMap));
      
      setServiceData({
        name_ar: editingService.name_ar || '',
        name_en: editingService.name_en || '',
        short_description_ar: editingService.short_description_ar || '',
        description_ar: editingService.description_ar || '',
        duration: editingService.duration || '',
        is_free: editingService.is_free !== false
      });
      setStep(3); // Start at delivery step in edit mode
    } else if (isOpen && contextProgramId) {
      setSelectedProgramId(contextProgramId);
    }
    if (isOpen && !editingService && contextCategoryId) {
      setSelectedCategoryId(contextCategoryId);
    }
  }, [isOpen, contextProgramId, contextCategoryId, editingService, categories]);
  
  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedProgramId(contextProgramId || '');
      setCreateNewProgram(false);
      setNewProgram({ name_ar: '', name_en: '' });
      setSelectedCategoryId(contextCategoryId || '');
      setCreateNewCategory(false);
      setNewCategory({ name_ar: '', name_en: '', category_type: 'training' });
      setDeliveryMethods([]);
      setServiceData({ name_ar: '', name_en: '', short_description_ar: '', description_ar: '', duration: '', is_free: true });
      setErrors({});
      setShowSuccessModal(false);
      setCreatedServiceName('');
    }
  }, [isOpen, contextProgramId, contextCategoryId]);
  
  if (!isOpen) return null;
  
  const STEPS = [
    { num: 1, label: 'البرنامج', icon: Folder },
    { num: 2, label: 'تصنيف الخدمة', icon: Tag },
    { num: 3, label: 'طريقة تقديم الخدمة', icon: MapPin },
    { num: 4, label: 'تفاصيل الخدمة', icon: FileText },
    { num: 5, label: 'المراجعة', icon: CheckCircle },
  ];
  
  const filteredCategories = categories.filter(c => c.program_id === selectedProgramId);
  
  const validateStep = () => {
    const errs = {};
    
    if (step === 1) {
      if (!selectedProgramId && !createNewProgram) errs.program = 'يجب اختيار برنامج';
      if (createNewProgram && !newProgram.name_ar.trim()) errs.program_name = 'اسم البرنامج مطلوب';
    }
    
    if (step === 2) {
      if (!selectedCategoryId && !createNewCategory) errs.category = 'يجب اختيار تصنيف';
      if (createNewCategory && !newCategory.name_ar.trim()) errs.category_name = 'اسم التصنيف مطلوب';
    }
    
    if (step === 3) {
      if (deliveryMethods.length === 0) errs.delivery = 'يجب تحديد طريقة تقديم واحدة على الأقل';
    }
    
    if (step === 4) {
      if (!serviceData.name_ar.trim()) errs.name_ar = 'اسم الخدمة مطلوب';
      if (!serviceData.short_description_ar.trim()) errs.short_description = 'الوصف المختصر مطلوب';
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  
  const handleNext = () => {
    if (validateStep()) {
      setStep(s => s + 1);
    }
  };
  
  const handleBack = () => {
    setStep(s => s - 1);
    setErrors({});
  };
  
  const toggleDelivery = (type) => {
    const exists = deliveryMethods.find(d => d.delivery_type === type);
    if (exists) {
      setDeliveryMethods(deliveryMethods.filter(d => d.delivery_type !== type));
    } else {
      setDeliveryMethods([...deliveryMethods, { delivery_type: type, cities: [], branch_name: '', meeting_link: '', is_active: true }]);
    }
  };
  
  const updateDelivery = (type, field, value) => {
    setDeliveryMethods(deliveryMethods.map(d => d.delivery_type === type ? { ...d, [field]: value } : d));
  };
  
  // Handle city added from modal
  const handleCityAdded = (newCity) => {
    setCities([...cities, newCity]);
    // Auto-select the new city for the pending delivery type
    if (pendingDeliveryType) {
      const dm = deliveryMethods.find(d => d.delivery_type === pendingDeliveryType);
      if (dm) {
        const updatedCities = [...(dm.cities || []), newCity.name_ar];
        updateDelivery(pendingDeliveryType, 'cities', updatedCities);
      }
      setPendingDeliveryType(null);
    }
  };
  
  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Convert multi-city to individual delivery methods for backend compatibility
      const expandedDeliveryMethods = [];
      deliveryMethods.forEach(dm => {
        if ((dm.delivery_type === 'branch' || dm.delivery_type === 'hybrid') && dm.cities?.length > 0) {
          dm.cities.forEach(city => {
            expandedDeliveryMethods.push({
              delivery_type: dm.delivery_type,
              city: city,
              branch_name: dm.branch_name || '',
              meeting_link: dm.meeting_link || '',
              is_active: true
            });
          });
        } else {
          expandedDeliveryMethods.push({
            delivery_type: dm.delivery_type,
            city: dm.cities?.[0] || '',
            branch_name: dm.branch_name || '',
            meeting_link: dm.meeting_link || '',
            is_active: true
          });
        }
      });
      
      if (isEditMode) {
        // Update existing service
        const updateData = {
          category_id: selectedCategoryId,
          ...serviceData,
          delivery_methods: expandedDeliveryMethods
        };
        await axios.put(`${API}/services/${editingService.service_id}`, updateData, { headers: getAuthHeaders() });
        setCreatedServiceName(serviceData.name_ar);
        setShowSuccessModal(true);
      } else {
        // Create new service
        const wizardData = {
          step1: {
            program_id: createNewProgram ? null : selectedProgramId,
            create_new: createNewProgram,
            new_program: createNewProgram ? newProgram : null
          },
          step2: {
            category_id: createNewCategory ? null : selectedCategoryId,
            create_new: createNewCategory,
            new_category: createNewCategory ? { ...newCategory, program_id: selectedProgramId } : null
          },
          step3: { delivery_methods: expandedDeliveryMethods },
          step4: serviceData
        };
        
        await axios.post(`${API}/wizard/create-service`, wizardData, { headers: getAuthHeaders() });
        setCreatedServiceName(serviceData.name_ar);
        setShowSuccessModal(true);
      }
    } catch (e) {
      setErrors({ submit: e.response?.data?.detail || 'حدث خطأ أثناء حفظ الخدمة' });
    }
    setLoading(false);
  };
  
  // Handle success modal actions
  const handleAddAnother = () => {
    setShowSuccessModal(false);
    setStep(4); // Go back to service details for new service
    setServiceData({ name_ar: '', name_en: '', short_description_ar: '', description_ar: '', duration: '', is_free: true });
    setDeliveryMethods([]);
    setStep(3); // Start from delivery step (keeping program/category)
    onComplete(); // Refresh data
  };
  
  const handleViewServices = () => {
    setShowSuccessModal(false);
    onComplete(selectedProgramId, createNewCategory ? null : selectedCategoryId); // Pass context back
    onClose();
  };
  
  const getSelectedProgramName = () => {
    if (createNewProgram) return `جديد: ${newProgram.name_ar}`;
    return programs.find(p => p.program_id === selectedProgramId)?.name_ar || '-';
  };
  
  const getSelectedCategoryName = () => {
    if (createNewCategory) return `جديد: ${newCategory.name_ar}`;
    return categories.find(c => c.category_id === selectedCategoryId)?.name_ar || '-';
  };
  
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000 }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '800px', maxWidth: '95vw', maxHeight: '90vh',
        backgroundColor: COLORS.white, borderRadius: '24px',
        boxShadow: '0 25px 80px rgba(0,0,0,0.3)', zIndex: 1001,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '24px 28px', borderBottom: `1px solid ${COLORS.border}`, backgroundColor: COLORS.bg }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: COLORS.text }}>{isEditMode ? 'تعديل الخدمة' : 'إضافة خدمة جديدة'}</h2>
              <p style={{ margin: '6px 0 0', fontSize: '14px', color: COLORS.textMuted }}>الخطوة {step} من 5</p>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}>
              <X size={24} color={COLORS.textMuted} />
            </button>
          </div>
          
          {/* Stepper */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const isActive = step === s.num;
              const isCompleted = step > s.num;
              return (
                <div key={s.num} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '12px',
                    backgroundColor: isCompleted ? COLORS.success : isActive ? COLORS.primary : COLORS.white,
                    border: `2px solid ${isCompleted ? COLORS.success : isActive ? COLORS.primary : COLORS.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: '8px', transition: 'all 0.3s',
                  }}>
                    {isCompleted ? <Check size={20} color={COLORS.white} /> : <Icon size={20} color={isActive ? COLORS.white : COLORS.textMuted} />}
                  </div>
                  <span style={{
                    fontSize: '12px', fontWeight: isActive ? 600 : 400,
                    color: isActive ? COLORS.primary : COLORS.textMuted,
                    textAlign: 'center', lineHeight: 1.3,
                  }}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '28px' }}>
          {errors.submit && <Alert type="error" style={{ marginBottom: '24px' }}>{errors.submit}</Alert>}
          
          {/* STEP 1: Program */}
          {step === 1 && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: COLORS.text }}>اختيار البرنامج</h3>
                <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.6 }}>
                  اختر البرنامج الذي تنتمي له الخدمة
                </p>
              </div>
              
              <Alert type="info" style={{ marginBottom: '24px' }}>
                البرنامج يُستخدم للتنظيم فقط ولا يؤثر على توصيات الذكاء الاصطناعي
              </Alert>
              
              {programs.length === 0 && !createNewProgram ? (
                <EmptyState
                  icon={Folder}
                  title="لا يوجد برامج"
                  description="قم بإنشاء برنامج أولاً للمتابعة"
                  action={
                    <Button variant="primary" icon={Plus} onClick={() => setCreateNewProgram(true)}>
                      إنشاء برنامج جديد
                    </Button>
                  }
                />
              ) : (
                <>
                  <FormField label="اختيار برنامج موجود" error={errors.program}>
                    <Select
                      value={selectedProgramId}
                      onChange={e => { setSelectedProgramId(e.target.value); setCreateNewProgram(false); }}
                      options={programs.map(p => ({ value: p.program_id, label: p.name_ar }))}
                      placeholder="-- اختر البرنامج --"
                      disabled={createNewProgram}
                    />
                  </FormField>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '20px 0' }}>
                    <div style={{ flex: 1, height: '1px', backgroundColor: COLORS.border }} />
                    <span style={{ fontSize: '13px', color: COLORS.textMuted }}>أو</span>
                    <div style={{ flex: 1, height: '1px', backgroundColor: COLORS.border }} />
                  </div>
                  
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '16px', borderRadius: '12px', cursor: 'pointer',
                    border: `2px solid ${createNewProgram ? COLORS.primary : COLORS.border}`,
                    backgroundColor: createNewProgram ? `${COLORS.primary}08` : COLORS.white,
                    marginBottom: '20px',
                  }}>
                    <div style={{
                      width: '24px', height: '24px', borderRadius: '6px',
                      border: `2px solid ${createNewProgram ? COLORS.primary : COLORS.border}`,
                      backgroundColor: createNewProgram ? COLORS.primary : COLORS.white,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {createNewProgram && <Check size={14} color={COLORS.white} />}
                    </div>
                    <input type="checkbox" checked={createNewProgram} onChange={e => { setCreateNewProgram(e.target.checked); if (e.target.checked) setSelectedProgramId(''); }} style={{ display: 'none' }} />
                    <span style={{ fontSize: '15px', fontWeight: 500, color: COLORS.text }}>إنشاء برنامج جديد</span>
                  </label>
                  
                  {createNewProgram && (
                    <div style={{ padding: '20px', backgroundColor: COLORS.bg, borderRadius: '12px' }}>
                      <FormField label="اسم البرنامج بالعربية" required error={errors.program_name}>
                        <Input
                          value={newProgram.name_ar}
                          onChange={e => setNewProgram({ ...newProgram, name_ar: e.target.value })}
                          placeholder="مثال: برنامج التمكين"
                          error={errors.program_name}
                        />
                      </FormField>
                      <FormField label="اسم البرنامج بالإنجليزية" hint="اختياري">
                        <Input
                          value={newProgram.name_en}
                          onChange={e => setNewProgram({ ...newProgram, name_en: e.target.value })}
                          placeholder="Example: Empowerment Program"
                        />
                      </FormField>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* STEP 2: Category */}
          {step === 2 && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: COLORS.text }}>تصنيف الخدمة</h3>
                <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.6 }}>
                  حدد نوع الخدمة لتوجيهها للفئة المناسبة من المستخدمين
                </p>
              </div>
              
              <Alert type="warning" style={{ marginBottom: '24px' }}>
                هذا التصنيف يساعد النظام على عرض الخدمة للمستخدم المناسب
              </Alert>
              
              <FormField label="اختيار تصنيف موجود" error={errors.category}>
                <Select
                  value={selectedCategoryId}
                  onChange={e => { setSelectedCategoryId(e.target.value); setCreateNewCategory(false); }}
                  options={filteredCategories.map(c => ({ value: c.category_id, label: c.name_ar }))}
                  placeholder={filteredCategories.length > 0 ? '-- اختر التصنيف --' : 'لا توجد تصنيفات في هذا البرنامج'}
                  disabled={createNewCategory || filteredCategories.length === 0}
                />
              </FormField>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '20px 0' }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: COLORS.border }} />
                <span style={{ fontSize: '13px', color: COLORS.textMuted }}>أو</span>
                <div style={{ flex: 1, height: '1px', backgroundColor: COLORS.border }} />
              </div>
              
              <label style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '16px', borderRadius: '12px', cursor: 'pointer',
                border: `2px solid ${createNewCategory ? COLORS.primary : COLORS.border}`,
                backgroundColor: createNewCategory ? `${COLORS.primary}08` : COLORS.white,
                marginBottom: '20px',
              }}>
                <div style={{
                  width: '24px', height: '24px', borderRadius: '6px',
                  border: `2px solid ${createNewCategory ? COLORS.primary : COLORS.border}`,
                  backgroundColor: createNewCategory ? COLORS.primary : COLORS.white,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {createNewCategory && <Check size={14} color={COLORS.white} />}
                </div>
                <input type="checkbox" checked={createNewCategory} onChange={e => { setCreateNewCategory(e.target.checked); if (e.target.checked) setSelectedCategoryId(''); }} style={{ display: 'none' }} />
                <span style={{ fontSize: '15px', fontWeight: 500, color: COLORS.text }}>إنشاء تصنيف جديد</span>
              </label>
              
              {createNewCategory && (
                <div style={{ padding: '20px', backgroundColor: COLORS.bg, borderRadius: '12px' }}>
                  <FormField label="اسم التصنيف بالعربية" required error={errors.category_name}>
                    <Input
                      value={newCategory.name_ar}
                      onChange={e => setNewCategory({ ...newCategory, name_ar: e.target.value })}
                      placeholder="مثال: الاستشارات المالية"
                      error={errors.category_name}
                    />
                  </FormField>
                  <FormField label="اسم التصنيف بالإنجليزية" hint="اختياري">
                    <Input
                      value={newCategory.name_en}
                      onChange={e => setNewCategory({ ...newCategory, name_en: e.target.value })}
                      placeholder="Example: Financial Consulting"
                    />
                  </FormField>
                  <FormField label="نوع الخدمة" required>
                    <Select
                      value={newCategory.category_type}
                      onChange={e => setNewCategory({ ...newCategory, category_type: e.target.value })}
                      options={CATEGORY_TYPES.map(t => ({ value: t.value, label: t.label }))}
                    />
                  </FormField>
                </div>
              )}
            </div>
          )}
          
          {/* STEP 3: Delivery */}
          {step === 3 && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: COLORS.text }}>طريقة تقديم الخدمة</h3>
                <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.6 }}>
                  حدد كيف سيتم تقديم هذه الخدمة للمستفيد
                </p>
              </div>
              
              {errors.delivery && <Alert type="error" style={{ marginBottom: '24px' }}>{errors.delivery}</Alert>}
              
              <Alert type="info" style={{ marginBottom: '24px' }}>
                يمكنك إضافة أكثر من طريقة تقديم
              </Alert>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '24px' }}>
                {DELIVERY_OPTIONS.map(opt => {
                  const isSelected = deliveryMethods.some(d => d.delivery_type === opt.value);
                  return (
                    <CheckboxCard
                      key={opt.value}
                      checked={isSelected}
                      onChange={() => toggleDelivery(opt.value)}
                      icon={opt.icon}
                      label={opt.label}
                      description={opt.description}
                      color={opt.color}
                    />
                  );
                })}
              </div>
              
              {/* Delivery Details */}
              {deliveryMethods.map(dm => {
                const opt = DELIVERY_OPTIONS.find(o => o.value === dm.delivery_type);
                return (
                  <div key={dm.delivery_type} style={{
                    padding: '20px', backgroundColor: COLORS.bg, borderRadius: '12px',
                    marginBottom: '12px', borderRight: `4px solid ${opt?.color}`,
                  }}>
                    <h4 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 600, color: COLORS.text, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {opt && <opt.icon size={18} color={opt.color} />}
                      تفاصيل: {opt?.label}
                    </h4>
                    
                    {(dm.delivery_type === 'branch' || dm.delivery_type === 'hybrid') && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <FormField label="المدن" required hint="يمكنك اختيار أكثر من مدينة">
                          <MultiCitySelector
                            selectedCities={dm.cities || []}
                            onChange={(selectedCities) => updateDelivery(dm.delivery_type, 'cities', selectedCities)}
                            cities={cities}
                            onAddCity={() => { setPendingDeliveryType(dm.delivery_type); setShowAddCityModal(true); }}
                          />
                        </FormField>
                        <FormField label="اسم الفرع" hint="اختياري">
                          <Input
                            value={dm.branch_name || ''}
                            onChange={e => updateDelivery(dm.delivery_type, 'branch_name', e.target.value)}
                            placeholder="مثال: الفرع الرئيسي"
                          />
                        </FormField>
                      </div>
                    )}
                    
                    {(dm.delivery_type === 'online' || dm.delivery_type === 'hybrid') && (
                      <FormField label="رابط الاجتماع" hint="اختياري">
                        <Input
                          value={dm.meeting_link || ''}
                          onChange={e => updateDelivery(dm.delivery_type, 'meeting_link', e.target.value)}
                          placeholder="https://meet.example.com/..."
                        />
                      </FormField>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {/* STEP 4: Service Details */}
          {step === 4 && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: COLORS.text }}>تفاصيل الخدمة</h3>
                <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.6 }}>
                  أدخل المعلومات التي ستظهر للمستفيد داخل هلا
                </p>
              </div>
              
              <Alert type="warning" style={{ marginBottom: '24px' }}>
                لن تظهر هذه الخدمة في نتائج هلا حتى يتم ربطها بقواعد التوصية
              </Alert>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <FormField label="اسم الخدمة بالعربية" required error={errors.name_ar}>
                  <Input
                    value={serviceData.name_ar}
                    onChange={e => setServiceData({ ...serviceData, name_ar: e.target.value })}
                    placeholder="مثال: استشارة تطوير الأعمال"
                    error={errors.name_ar}
                  />
                </FormField>
                <FormField label="اسم الخدمة بالإنجليزية" hint="اختياري">
                  <Input
                    value={serviceData.name_en}
                    onChange={e => setServiceData({ ...serviceData, name_en: e.target.value })}
                    placeholder="Example: Business Development Consultation"
                  />
                </FormField>
              </div>
              
              <FormField label="مدة الخدمة" hint="اختياري">
                <Input
                  value={serviceData.duration}
                  onChange={e => setServiceData({ ...serviceData, duration: e.target.value })}
                  placeholder="مثال: 60 دقيقة"
                />
              </FormField>
              
              <FormField label="وصف مختصر" required hint="سيظهر في قائمة النتائج" error={errors.short_description}>
                <Input
                  value={serviceData.short_description_ar}
                  onChange={e => setServiceData({ ...serviceData, short_description_ar: e.target.value })}
                  placeholder="وصف موجز للخدمة في سطر واحد..."
                  error={errors.short_description}
                />
              </FormField>
              
              <FormField label="وصف تفصيلي" hint="اختياري - سيظهر في صفحة التفاصيل">
                <Textarea
                  value={serviceData.description_ar}
                  onChange={e => setServiceData({ ...serviceData, description_ar: e.target.value })}
                  placeholder="وصف تفصيلي للخدمة ومزاياها..."
                  rows={4}
                />
              </FormField>
            </div>
          )}
          
          {/* STEP 5: Review */}
          {step === 5 && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: COLORS.text }}>مراجعة قبل الحفظ</h3>
                <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted, lineHeight: 1.6 }}>
                  تأكد من صحة البيانات قبل إنشاء الخدمة
                </p>
              </div>
              
              <Alert type="warning" style={{ marginBottom: '24px' }}>
                هذه الخدمة غير مرتبطة بمحرك التوصيات ولن تظهر للمستخدم حتى يتم ربطها
              </Alert>
              
              {/* Review Cards */}
              <div style={{ display: 'grid', gap: '16px' }}>
                <ReviewCard
                  title="البرنامج"
                  value={getSelectedProgramName()}
                  icon={Folder}
                  badge="للتنظيم فقط"
                  badgeColor={COLORS.info}
                  onEdit={() => setStep(1)}
                />
                <ReviewCard
                  title="التصنيف"
                  value={getSelectedCategoryName()}
                  icon={Tag}
                  badge="يستخدمه AI"
                  badgeColor={COLORS.warning}
                  onEdit={() => setStep(2)}
                />
                <ReviewCard
                  title="طريقة التقديم"
                  value={deliveryMethods.map(d => DELIVERY_OPTIONS.find(o => o.value === d.delivery_type)?.label).join('، ') || '-'}
                  icon={MapPin}
                  onEdit={() => setStep(3)}
                />
                <ReviewCard
                  title="الخدمة"
                  value={serviceData.name_ar}
                  subtitle={serviceData.short_description_ar}
                  icon={FileText}
                  badge="يستخدمه AI"
                  badgeColor={COLORS.warning}
                  onEdit={() => setStep(4)}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div style={{ padding: '20px 28px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', backgroundColor: COLORS.bg }}>
          <Button variant="outline" onClick={step > 1 ? handleBack : onClose} icon={step > 1 ? ArrowRight : X}>
            {step > 1 ? 'السابق' : 'إلغاء'}
          </Button>
          
          {step < 5 ? (
            <Button variant="primary" onClick={handleNext} icon={ArrowLeft}>
              التالي
            </Button>
          ) : (
            <Button variant="success" onClick={handleSubmit} loading={loading} icon={CheckCircle}>
              {isEditMode ? 'حفظ التعديلات' : 'إنشاء الخدمة'}
            </Button>
          )}
        </div>
      </div>
      
      {/* Add City Modal */}
      <AddCityModal
        isOpen={showAddCityModal}
        onClose={() => { setShowAddCityModal(false); setPendingDeliveryType(null); }}
        onCityAdded={handleCityAdded}
      />
      
      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onAddAnother={isEditMode ? null : handleAddAnother}
        onViewServices={handleViewServices}
        serviceName={createdServiceName}
        isEdit={isEditMode}
      />
    </>
  );
}

// Review Card Component
function ReviewCard({ title, value, subtitle, icon: Icon, badge, badgeColor, onEdit }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '16px',
      padding: '20px', backgroundColor: COLORS.white, borderRadius: '12px',
      border: `1px solid ${COLORS.border}`,
    }}>
      <div style={{
        width: '44px', height: '44px', borderRadius: '10px',
        backgroundColor: COLORS.bg, display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={22} color={COLORS.primary} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{ fontSize: '13px', color: COLORS.textMuted }}>{title}</span>
          {badge && <Badge color={badgeColor}>{badge}</Badge>}
        </div>
        <p style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>{value}</p>
        {subtitle && <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>{subtitle}</p>}
      </div>
      <Button variant="ghost" size="sm" icon={Edit2} onClick={onEdit}>تعديل</Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DATA RESET MODAL
// ═══════════════════════════════════════════════════════════════════════════════

function DataResetModal({ isOpen, onClose, onResetComplete }) {
  const [confirmed, setConfirmed] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!isOpen) {
      setConfirmed(false);
      setResult(null);
      setError(null);
    }
  }, [isOpen]);
  
  const handleReset = async () => {
    if (!confirmed) return;
    
    setResetting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/reset-data?confirm=true`, {}, { headers: getAuthHeaders() });
      setResult(response.data);
      
      // Clear local storage for branches
      localStorage.removeItem('hala_branches');
      
      if (onResetComplete) {
        onResetComplete();
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'فشل في إعادة تعيين البيانات');
    }
    setResetting(false);
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000 }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '500px', maxWidth: '95vw',
        backgroundColor: COLORS.white, borderRadius: '20px',
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)', zIndex: 1001,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '24px', 
          background: `linear-gradient(135deg, ${COLORS.error} 0%, #B91C1C 100%)`,
          color: COLORS.white,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '52px', height: '52px', borderRadius: '14px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ShieldAlert size={28} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>تأكيد إعادة تعيين البيانات</h2>
              <p style={{ margin: '4px 0 0', fontSize: '14px', opacity: 0.9 }}>عملية لا يمكن التراجع عنها</p>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div style={{ padding: '24px' }}>
          {!result ? (
            <>
              {/* Warning Message */}
              <div style={{
                padding: '16px', borderRadius: '12px',
                backgroundColor: '#FEF2F2', border: '1px solid #FECACA',
                marginBottom: '20px',
              }}>
                <p style={{ margin: 0, fontSize: '15px', color: '#991B1B', lineHeight: 1.7 }}>
                  <strong>تحذير:</strong> سيتم حذف جميع البيانات الحالية بما في ذلك:
                </p>
                <ul style={{ margin: '12px 0 0', paddingRight: '20px', color: '#991B1B', fontSize: '14px', lineHeight: 1.8 }}>
                  <li>جميع الخدمات والتصنيفات والبرامج</li>
                  <li>جميع الموارد (المستشارون، البرامج التدريبية، الفعاليات، إلخ)</li>
                  <li>قواعد محرك التوصيات والاحتياجات</li>
                  <li>المدن والفروع</li>
                </ul>
              </div>
              
              {/* What won't be deleted */}
              <div style={{
                padding: '16px', borderRadius: '12px',
                backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0',
                marginBottom: '24px',
              }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#166534' }}>
                  <strong>لن يتم حذف:</strong> حسابات المدراء، إعدادات النظام، هيكل قاعدة البيانات
                </p>
              </div>
              
              {error && (
                <Alert type="error" style={{ marginBottom: '20px' }}>
                  {error}
                </Alert>
              )}
              
              {/* Confirmation Checkbox */}
              <label style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '16px', borderRadius: '12px',
                backgroundColor: confirmed ? `${COLORS.error}08` : COLORS.bg,
                border: `2px solid ${confirmed ? COLORS.error : COLORS.border}`,
                cursor: 'pointer', transition: 'all 0.2s',
              }}>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  style={{ width: '20px', height: '20px', accentColor: COLORS.error }}
                />
                <span style={{ fontSize: '15px', fontWeight: 600, color: confirmed ? COLORS.error : COLORS.text }}>
                  أنا متأكد من حذف جميع البيانات
                </span>
              </label>
            </>
          ) : (
            /* Success Result */
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{
                width: '72px', height: '72px', borderRadius: '50%',
                backgroundColor: `${COLORS.success}15`, margin: '0 auto 20px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CheckCircle size={36} color={COLORS.success} />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 700, color: COLORS.text }}>
                {result.message}
              </h3>
              <p style={{ margin: '0 0 20px', fontSize: '15px', color: COLORS.textMuted }}>
                تم حذف {result.total_deleted} عنصر
              </p>
              
              {/* Details */}
              <div style={{
                padding: '16px', borderRadius: '12px',
                backgroundColor: COLORS.bg, textAlign: 'right',
              }}>
                <p style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: COLORS.text }}>تفاصيل الحذف:</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {Object.entries(result.details || {}).map(([key, count]) => (
                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: COLORS.textMuted }}>
                        {key === 'services' ? 'الخدمات' :
                         key === 'categories' ? 'التصنيفات' :
                         key === 'programs' ? 'البرامج' :
                         key === 'service_resources' ? 'الموارد' :
                         key === 'recommendation_rules' ? 'قواعد التوصية' :
                         key === 'recommendation_needs' ? 'الاحتياجات' :
                         key === 'cities' ? 'المدن' : key}
                      </span>
                      <span style={{ fontWeight: 600, color: COLORS.text }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: '12px',
          backgroundColor: COLORS.bg,
        }}>
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose}>إلغاء</Button>
              <Button
                variant="danger"
                icon={RotateCcw}
                onClick={handleReset}
                disabled={!confirmed || resetting}
                loading={resetting}
              >
                تأكيد الحذف
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={onClose}>إغلاق</Button>
          )}
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// BULK IMPORT MODAL
// ═══════════════════════════════════════════════════════════════════════════════

function BulkImportModal({ isOpen, onClose, onImportComplete }) {
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(1); // 1: upload, 2: preview, 3: importing, 4: success
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const STEPS = [
    { num: 1, label: 'اختيار الملف', icon: Upload, description: 'رفع ملف Excel أو CSV أو JSON' },
    { num: 2, label: 'معاينة وتحقق', icon: Eye, description: 'مراجعة البيانات قبل الاستيراد' },
    { num: 3, label: 'استيراد', icon: Download, description: 'إضافة البيانات للنظام' },
    { num: 4, label: 'اكتمال', icon: CheckCircle, description: 'تم الاستيراد بنجاح' },
  ];
  
  const FILE_TYPES = [
    { ext: '.xlsx', label: 'Excel', icon: FileSpreadsheet, color: '#059669', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    { ext: '.csv', label: 'CSV', icon: Table2, color: '#2563EB', mime: 'text/csv' },
    { ext: '.json', label: 'JSON', icon: FileJson, color: '#D97706', mime: 'application/json' },
  ];
  
  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setFile(null);
      setFileType(null);
      setPreviewData(null);
      setValidationResults(null);
      setCurrentStep(1);
      setError(null);
      setUploadProgress(0);
      setIsProcessing(false);
    }
  }, [isOpen]);
  
  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    const ext = '.' + selectedFile.name.split('.').pop().toLowerCase();
    const type = FILE_TYPES.find(t => t.ext === ext);
    
    if (!type) {
      setError('نوع الملف غير مدعوم. الرجاء استخدام Excel أو CSV أو JSON');
      return;
    }
    
    setFile(selectedFile);
    setFileType(type);
    setError(null);
  };
  
  const handleNextStep = async () => {
    if (currentStep === 1 && file) {
      // Validate file
      await validateFileWithAPI(file);
    } else if (currentStep === 2 && validationResults?.valid > 0) {
      // Start import
      await handleImport();
    }
  };
  
  const handlePrevStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setValidationResults(null);
      setPreviewData(null);
      setError(null);
    }
  };
  
  const validateFileWithAPI = async (fileToValidate) => {
    setIsProcessing(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', fileToValidate);
      
      const token = localStorage.getItem('hala_admin_token');
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 15, 90));
      }, 200);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catalog/import/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'فشل في التحقق من الملف');
      }
      
      const result = await response.json();
      
      setPreviewData(result.preview || []);
      setValidationResults({
        total: result.total,
        valid: result.valid,
        invalid: result.invalid,
        errors: result.errors || [],
        warnings: result.warnings || [],
      });
      
      // Move to preview step
      setTimeout(() => {
        setCurrentStep(2);
        setIsProcessing(false);
        setUploadProgress(0);
      }, 500);
      
    } catch (e) {
      setError('فشل في التحقق: ' + (e.message || 'خطأ غير معروف'));
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };
  
  const handleImport = async () => {
    if (!file) return;
    
    setCurrentStep(3);
    setImporting(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('hala_admin_token');
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 8, 90));
      }, 200);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catalog/import/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'فشل الاستيراد');
      }
      
      const result = await response.json();
      
      setValidationResults(prev => ({
        ...prev,
        imported: result.imported,
        failed: result.failed,
      }));
      
      setTimeout(() => {
        setCurrentStep(4);
        setImporting(false);
        if (onImportComplete) {
          onImportComplete(result.imported);
        }
      }, 500);
      
    } catch (e) {
      setError('فشل في الاستيراد: ' + (e.message || 'خطأ غير معروف'));
      setCurrentStep(2);
      setImporting(false);
      setUploadProgress(0);
    }
  };
  
  const handleDownloadTemplate = (type) => {
    let content, filename, mimeType;
    
    if (type === 'json') {
      content = JSON.stringify([
        {
          name_ar: "اسم الخدمة بالعربية",
          name_en: "Service Name in English",
          category_name_ar: "اسم التصنيف",
          program_name_ar: "اسم البرنامج",
          description_ar: "وصف الخدمة",
          delivery_methods: ["branch", "online"],
          is_active: true
        }
      ], null, 2);
      filename = 'import_template.json';
      mimeType = 'application/json';
    } else {
      content = 'name_ar,name_en,category_name_ar,program_name_ar,description_ar,delivery_methods,is_active\n';
      content += '"اسم الخدمة","Service Name","اسم التصنيف","اسم البرنامج","وصف الخدمة","branch,online","true"\n';
      filename = 'import_template.csv';
      mimeType = 'text/csv';
    }
    
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const resetAndStartOver = () => {
    setFile(null);
    setFileType(null);
    setPreviewData(null);
    setValidationResults(null);
    setCurrentStep(1);
    setError(null);
    setUploadProgress(0);
    setIsProcessing(false);
  };
  
  const canProceed = () => {
    if (currentStep === 1) return file !== null && !isProcessing;
    if (currentStep === 2) return validationResults?.valid > 0 && validationResults?.invalid === 0;
    return false;
  };
  
  if (!isOpen) return null;
  
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000 }} onClick={onClose} />
      <div data-testid="bulk-import-modal" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '800px', maxWidth: '95vw', maxHeight: '90vh',
        backgroundColor: COLORS.white, borderRadius: '24px',
        boxShadow: '0 25px 80px rgba(0,0,0,0.3)', zIndex: 1001,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header with Step Progress */}
        <div style={{ 
          padding: '24px 28px', 
          borderBottom: `1px solid ${COLORS.border}`,
          background: `linear-gradient(135deg, ${COLORS.primary}08 0%, ${COLORS.white} 100%)`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: COLORS.text }}>
                استيراد البيانات
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: '14px', color: COLORS.textMuted }}>
                {STEPS[currentStep - 1]?.description}
              </p>
            </div>
            <button 
              data-testid="close-import-modal"
              onClick={onClose} 
              style={{
                width: '40px', height: '40px', borderRadius: '12px', border: 'none',
                backgroundColor: COLORS.bg, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
              }}
            >
              <X size={22} color={COLORS.textMuted} />
            </button>
          </div>
          
          {/* Step Progress Indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.num;
              const isCompleted = currentStep > step.num;
              const isLast = index === STEPS.length - 1;
              
              return (
                <div key={step.num} style={{ display: 'flex', alignItems: 'center', flex: isLast ? 'none' : 1 }}>
                  {/* Step Circle */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    minWidth: '80px',
                  }}>
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '14px',
                      backgroundColor: isCompleted ? COLORS.success : isActive ? COLORS.primary : COLORS.bg,
                      border: `2px solid ${isCompleted ? COLORS.success : isActive ? COLORS.primary : COLORS.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      boxShadow: isActive ? `0 4px 12px ${COLORS.primary}30` : 'none',
                    }}>
                      {isCompleted ? (
                        <Check size={22} color={COLORS.white} strokeWidth={3} />
                      ) : (
                        <StepIcon size={22} color={isActive ? COLORS.white : COLORS.textMuted} />
                      )}
                    </div>
                    <span style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? COLORS.primary : isCompleted ? COLORS.success : COLORS.textMuted,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                    }}>
                      {step.label}
                    </span>
                  </div>
                  
                  {/* Connector Line */}
                  {!isLast && (
                    <div style={{
                      flex: 1,
                      height: '3px',
                      backgroundColor: isCompleted ? COLORS.success : COLORS.border,
                      marginBottom: '28px',
                      marginLeft: '8px',
                      marginRight: '8px',
                      borderRadius: '2px',
                      transition: 'background-color 0.3s ease',
                    }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Progress Bar for Processing */}
        {(isProcessing || importing) && (
          <div style={{ 
            padding: '16px 28px', 
            backgroundColor: `${COLORS.primary}08`,
            borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Loader2 size={18} color={COLORS.primary} style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: '14px', fontWeight: 600, color: COLORS.text }}>
                  {importing ? 'جاري استيراد البيانات...' : 'جاري التحقق من الملف...'}
                </span>
              </div>
              <span style={{ fontSize: '14px', fontWeight: 700, color: COLORS.primary }}>{uploadProgress}%</span>
            </div>
            <div style={{ 
              height: '8px', 
              backgroundColor: COLORS.border, 
              borderRadius: '4px', 
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', 
                backgroundColor: COLORS.primary, 
                borderRadius: '4px',
                width: `${uploadProgress}%`, 
                transition: 'width 0.3s ease',
                background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.gold})`,
              }} />
            </div>
            {file && (
              <p style={{ margin: '10px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
                {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
        )}
        
        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '28px' }}>
          {error && (
            <Alert type="error" style={{ marginBottom: '24px' }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {/* Step 1: Upload */}
          {currentStep === 1 && !isProcessing && (
            <div>
              <Alert type="info" style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Info size={18} />
                  <span><strong>الملفات المدعومة:</strong> Excel (.xlsx) أو CSV أو JSON</span>
                </div>
              </Alert>
              
              {/* File Selection Area */}
              {!file ? (
                <label data-testid="file-dropzone" style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: '60px 40px', 
                  border: `3px dashed ${COLORS.primary}40`, 
                  borderRadius: '20px',
                  backgroundColor: `${COLORS.primary}04`, 
                  cursor: 'pointer', 
                  transition: 'all 0.3s',
                  marginBottom: '28px',
                }}>
                  <div style={{
                    width: '88px', height: '88px', borderRadius: '50%',
                    backgroundColor: `${COLORS.primary}15`, 
                    display: 'flex',
                    alignItems: 'center', justifyContent: 'center', 
                    marginBottom: '20px',
                    boxShadow: `0 8px 24px ${COLORS.primary}20`,
                  }}>
                    <Upload size={40} color={COLORS.primary} />
                  </div>
                  <p style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 700, color: COLORS.text }}>
                    اختر ملف أو اسحبه هنا
                  </p>
                  <p style={{ margin: 0, fontSize: '15px', color: COLORS.textMuted }}>
                    Excel (.xlsx) أو CSV أو JSON
                  </p>
                  <input 
                    data-testid="file-input"
                    type="file" 
                    accept=".xlsx,.xls,.csv,.json" 
                    onChange={handleFileSelect} 
                    style={{ display: 'none' }} 
                  />
                </label>
              ) : (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '16px', padding: '20px',
                  backgroundColor: `${COLORS.success}08`, borderRadius: '16px', marginBottom: '28px',
                  border: `2px solid ${COLORS.success}30`,
                }}>
                  <div style={{
                    width: '56px', height: '56px', borderRadius: '14px',
                    backgroundColor: fileType?.color + '20',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {fileType && <fileType.icon size={28} color={fileType.color} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: COLORS.text }}>{file.name}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
                      {(file.size / 1024).toFixed(1)} KB • {fileType?.label}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <CheckCircle size={24} color={COLORS.success} />
                    <button 
                      onClick={() => { setFile(null); setFileType(null); }}
                      style={{
                        padding: '8px 16px', borderRadius: '8px', border: `1px solid ${COLORS.border}`,
                        backgroundColor: COLORS.white, cursor: 'pointer', fontSize: '13px',
                        color: COLORS.textMuted, fontWeight: 500,
                      }}
                    >
                      تغيير
                    </button>
                  </div>
                </div>
              )}
              
              {/* Supported File Types */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '28px' }}>
                {FILE_TYPES.map(type => (
                  <div key={type.ext} style={{
                    padding: '20px', borderRadius: '16px', 
                    border: `2px solid ${fileType?.ext === type.ext ? type.color : COLORS.border}`,
                    backgroundColor: fileType?.ext === type.ext ? `${type.color}08` : COLORS.white, 
                    textAlign: 'center',
                    transition: 'all 0.2s',
                  }}>
                    <type.icon size={32} color={type.color} style={{ marginBottom: '12px' }} />
                    <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: COLORS.text }}>{type.label}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: COLORS.textMuted }}>{type.ext}</p>
                  </div>
                ))}
              </div>
              
              {/* Download Template */}
              <div style={{
                padding: '20px', backgroundColor: `${COLORS.info}06`, borderRadius: '16px',
                border: `1px solid ${COLORS.info}25`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <Download size={20} color={COLORS.info} />
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: COLORS.text }}>
                    تحميل قالب أو تصدير البيانات الحالية
                  </p>
                </div>
                <p style={{ margin: '0 0 14px', fontSize: '13px', color: COLORS.textMuted }}>
                  قم بتصدير البيانات الحالية للاطلاع على الصيغة الصحيحة، أو استخدم القالب الفارغ
                </p>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <Button variant="primary" size="sm" icon={Download} onClick={async () => {
                    try {
                      const token = localStorage.getItem('hala_admin_token');
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catalog/export?format=json`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'hala_services_export.json';
                      a.click();
                      window.URL.revokeObjectURL(url);
                    } catch (e) { alert('خطأ في التصدير'); }
                  }}>
                    تصدير البيانات الحالية (JSON)
                  </Button>
                  <Button variant="outline" size="sm" icon={FileJson} onClick={() => handleDownloadTemplate('json')}>
                    قالب فارغ JSON
                  </Button>
                  <Button variant="outline" size="sm" icon={Table2} onClick={() => handleDownloadTemplate('csv')}>
                    قالب فارغ CSV
                  </Button>
                </div>
                
                {/* Arabic Encoding Note */}
                <div style={{ 
                  marginTop: '14px', padding: '12px', backgroundColor: `${COLORS.warning}10`, 
                  borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px'
                }}>
                  <AlertTriangle size={18} color={COLORS.warning} />
                  <p style={{ margin: 0, fontSize: '12px', color: COLORS.text, lineHeight: 1.5 }}>
                    <strong>ملاحظة:</strong> لدعم اللغة العربية، تأكد من حفظ الملف بترميز UTF-8. 
                    في Excel: احفظ كـ CSV UTF-8. في Notepad: احفظ بترميز UTF-8.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 1: Processing */}
          {currentStep === 1 && isProcessing && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{
                width: '100px', height: '100px', borderRadius: '50%',
                backgroundColor: `${COLORS.primary}12`, margin: '0 auto 28px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Loader2 size={48} color={COLORS.primary} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
              <h3 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 700, color: COLORS.text }}>
                جاري التحقق من الملف
              </h3>
              <p style={{ margin: 0, fontSize: '16px', color: COLORS.textMuted }}>
                يرجى الانتظار بينما يتم فحص البيانات...
              </p>
            </div>
          )}
          
          {/* Step 2: Preview & Validation */}
          {currentStep === 2 && (
            <div>
              {/* File Info Card */}
              {file && fileType && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '16px', padding: '18px',
                  backgroundColor: `${COLORS.success}08`, borderRadius: '14px', marginBottom: '24px',
                  border: `1px solid ${COLORS.success}25`,
                }}>
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px',
                    backgroundColor: fileType.color + '20',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <fileType.icon size={24} color={fileType.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: COLORS.text }}>{file.name}</p>
                    <p style={{ margin: '2px 0 0', fontSize: '13px', color: COLORS.textMuted }}>
                      {(file.size / 1024).toFixed(1)} KB • تم التحقق بنجاح
                    </p>
                  </div>
                  <CheckCircle size={26} color={COLORS.success} />
                </div>
              )}
              
              {/* Validation Results Cards */}
              {validationResults && (
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: COLORS.text }}>
                    نتائج التحقق
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
                    <div style={{ 
                      padding: '20px', backgroundColor: COLORS.bg, borderRadius: '14px', textAlign: 'center',
                      border: `1px solid ${COLORS.border}`,
                    }}>
                      <p style={{ margin: 0, fontSize: '32px', fontWeight: 800, color: COLORS.primary }}>{validationResults.total}</p>
                      <p style={{ margin: '6px 0 0', fontSize: '14px', color: COLORS.textMuted, fontWeight: 500 }}>إجمالي السجلات</p>
                    </div>
                    <div style={{ 
                      padding: '20px', backgroundColor: `${COLORS.success}10`, borderRadius: '14px', textAlign: 'center',
                      border: `1px solid ${COLORS.success}25`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <CircleCheck size={24} color={COLORS.success} />
                        <p style={{ margin: 0, fontSize: '32px', fontWeight: 800, color: COLORS.success }}>{validationResults.valid}</p>
                      </div>
                      <p style={{ margin: '6px 0 0', fontSize: '14px', color: COLORS.success, fontWeight: 500 }}>صالح للاستيراد</p>
                    </div>
                    <div style={{ 
                      padding: '20px', 
                      backgroundColor: validationResults.invalid > 0 ? `${COLORS.error}10` : COLORS.bg, 
                      borderRadius: '14px', textAlign: 'center',
                      border: `1px solid ${validationResults.invalid > 0 ? COLORS.error + '25' : COLORS.border}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        {validationResults.invalid > 0 && <CircleX size={24} color={COLORS.error} />}
                        <p style={{ margin: 0, fontSize: '32px', fontWeight: 800, color: validationResults.invalid > 0 ? COLORS.error : COLORS.textMuted }}>
                          {validationResults.invalid}
                        </p>
                      </div>
                      <p style={{ margin: '6px 0 0', fontSize: '14px', color: validationResults.invalid > 0 ? COLORS.error : COLORS.textMuted, fontWeight: 500 }}>
                        يحتاج تصحيح
                      </p>
                    </div>
                  </div>
                  
                  {/* Errors */}
                  {validationResults.errors?.length > 0 && (
                    <Alert type="error" style={{ marginBottom: '16px' }}>
                      <div>
                        <strong style={{ fontSize: '14px' }}>أخطاء يجب إصلاحها قبل الاستيراد:</strong>
                        <ul style={{ margin: '10px 0 0', paddingRight: '20px' }}>
                          {validationResults.errors.slice(0, 5).map((e, i) => (
                            <li key={i} style={{ fontSize: '13px', marginBottom: '6px', lineHeight: 1.5 }}>{e.message}</li>
                          ))}
                          {validationResults.errors.length > 5 && (
                            <li style={{ fontSize: '13px', color: COLORS.textMuted }}>
                              و {validationResults.errors.length - 5} أخطاء أخرى...
                            </li>
                          )}
                        </ul>
                      </div>
                    </Alert>
                  )}
                  
                  {/* Warnings */}
                  {validationResults.warnings?.length > 0 && (
                    <Alert type="warning">
                      <div>
                        <strong style={{ fontSize: '14px' }}>تنبيهات:</strong>
                        <ul style={{ margin: '10px 0 0', paddingRight: '20px' }}>
                          {validationResults.warnings.slice(0, 3).map((w, i) => (
                            <li key={i} style={{ fontSize: '13px', marginBottom: '6px', lineHeight: 1.5 }}>{w.message}</li>
                          ))}
                        </ul>
                      </div>
                    </Alert>
                  )}
                </div>
              )}
              
              {/* Preview Table */}
              {previewData && previewData.length > 0 && !previewData[0]?.message && (
                <div>
                  <h4 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: COLORS.text }}>
                    معاينة البيانات (أول {Math.min(5, previewData.length)} سجلات)
                  </h4>
                  <div style={{ overflowX: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: '12px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: COLORS.bg }}>
                          {Object.keys(previewData[0]).slice(0, 5).map(key => (
                            <th key={key} style={{ 
                              padding: '14px 16px', textAlign: 'right', fontWeight: 600, 
                              color: COLORS.text, borderBottom: `2px solid ${COLORS.border}`,
                              whiteSpace: 'nowrap',
                            }}>
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.slice(0, 5).map((row, i) => (
                          <tr key={i} style={{ backgroundColor: i % 2 === 0 ? COLORS.white : COLORS.bg }}>
                            {Object.values(row).slice(0, 5).map((val, j) => (
                              <td key={j} style={{ 
                                padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}`, 
                                color: COLORS.text, maxWidth: '200px', overflow: 'hidden', 
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {typeof val === 'object' ? JSON.stringify(val) : String(val || '-')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {previewData.length > 5 && (
                    <p style={{ margin: '14px 0 0', fontSize: '13px', color: COLORS.textMuted, textAlign: 'center' }}>
                      و {previewData.length - 5} سجلات أخرى...
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Step 3: Importing */}
          {currentStep === 3 && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{
                width: '100px', height: '100px', borderRadius: '50%',
                backgroundColor: `${COLORS.primary}12`, margin: '0 auto 28px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Loader2 size={48} color={COLORS.primary} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
              <h3 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 700, color: COLORS.text }}>
                جاري استيراد البيانات
              </h3>
              <p style={{ margin: 0, fontSize: '16px', color: COLORS.textMuted }}>
                يتم الآن إنشاء الخدمات من الملف...
              </p>
            </div>
          )}
          
          {/* Step 4: Success */}
          {currentStep === 4 && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{
                width: '100px', height: '100px', borderRadius: '50%',
                backgroundColor: `${COLORS.success}12`, margin: '0 auto 28px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 8px 24px ${COLORS.success}20`,
              }}>
                <CheckCircle size={52} color={COLORS.success} />
              </div>
              <h3 style={{ margin: '0 0 12px', fontSize: '26px', fontWeight: 800, color: COLORS.text }}>
                تم الاستيراد بنجاح!
              </h3>
              <p style={{ margin: '0 0 32px', fontSize: '17px', color: COLORS.textMuted, lineHeight: 1.6 }}>
                تم استيراد <strong style={{ color: COLORS.success, fontSize: '20px' }}>{validationResults?.imported || 0}</strong> خدمة بنجاح
                {validationResults?.failed > 0 && (
                  <span style={{ color: COLORS.error }}><br/>فشل استيراد {validationResults.failed} سجل</span>
                )}
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <Button variant="outline" size="lg" icon={Plus} onClick={resetAndStartOver}>
                  استيراد ملف آخر
                </Button>
                <Button variant="success" size="lg" icon={CheckCircle} onClick={onClose}>
                  إغلاق
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer - Navigation Buttons */}
        {currentStep < 4 && !isProcessing && !importing && (
          <div style={{
            padding: '20px 28px', borderTop: `1px solid ${COLORS.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            backgroundColor: COLORS.bg,
          }}>
            <div>
              {currentStep > 1 && (
                <Button 
                  data-testid="prev-step-btn"
                  variant="outline" 
                  icon={ArrowRight} 
                  onClick={handlePrevStep}
                >
                  السابق
                </Button>
              )}
              {currentStep === 1 && (
                <Button variant="ghost" onClick={onClose}>إلغاء</Button>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              {currentStep === 2 && validationResults?.invalid > 0 && (
                <span style={{ fontSize: '13px', color: COLORS.error, fontWeight: 500 }}>
                  يجب إصلاح الأخطاء أولاً
                </span>
              )}
              <Button
                data-testid="next-step-btn"
                variant={currentStep === 2 ? 'success' : 'primary'}
                size="lg"
                icon={currentStep === 2 ? Upload : ArrowLeft}
                onClick={handleNextStep}
                disabled={!canProceed()}
              >
                {currentStep === 1 ? 'التالي - التحقق من البيانات' : `استيراد ${validationResults?.valid || 0} خدمة`}
              </Button>
            </div>
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════

export default function CatalogPageV2() {
  const [stats, setStats] = useState({});
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(false);
  
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [showWizard, setShowWizard] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExcelImport, setShowExcelImport] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  
  // Fetch functions
  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats`, { headers: getAuthHeaders() });
      setStats(res.data);
    } catch (e) { console.error(e); }
  }, []);
  
  const fetchPrograms = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/programs`, { headers: getAuthHeaders() });
      setPrograms(res.data);
    } catch (e) { console.error(e); }
  }, []);
  
  const fetchCategories = useCallback(async () => {
    try {
      const url = selectedProgramId ? `${API}/categories?program_id=${selectedProgramId}` : `${API}/categories`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setCategories(res.data);
    } catch (e) { console.error(e); }
  }, [selectedProgramId]);
  
  const fetchServices = useCallback(async () => {
    setServicesLoading(true);
    try {
      let url = `${API}/services?`;
      if (selectedCategoryId) url += `category_id=${selectedCategoryId}&`;
      else if (selectedProgramId) url += `program_id=${selectedProgramId}&`;
      if (searchQuery) url += `search=${encodeURIComponent(searchQuery)}&`;
      
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setServices(res.data.items || []);
    } catch (e) { console.error(e); }
    setServicesLoading(false);
  }, [selectedProgramId, selectedCategoryId, searchQuery]);
  
  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await axios.post(`${API}/seed`, {}, { headers: getAuthHeaders() }).catch(() => {});
      await Promise.all([fetchStats(), fetchPrograms()]);
      setLoading(false);
    };
    init();
  }, [fetchStats, fetchPrograms]);
  
  useEffect(() => { fetchCategories(); }, [selectedProgramId, fetchCategories]);
  useEffect(() => { fetchServices(); }, [selectedProgramId, selectedCategoryId, searchQuery, fetchServices]);
  
  // Handlers
  const handleSaveProgram = async (data) => {
    if (editingProgram) {
      await axios.put(`${API}/programs/${editingProgram.program_id}`, data, { headers: getAuthHeaders() });
    } else {
      await axios.post(`${API}/programs`, data, { headers: getAuthHeaders() });
    }
    fetchPrograms();
    fetchStats();
  };
  
  const handleToggleProgram = async (prog) => {
    try {
      await axios.patch(`${API}/programs/${prog.program_id}/toggle`, {}, { headers: getAuthHeaders() });
      fetchPrograms();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ'); }
  };
  
  const handleDeleteProgram = async (prog) => {
    if (!window.confirm(`هل أنت متأكد من حذف البرنامج "${prog.name_ar}"؟\n\nلا يمكن التراجع عن هذا الإجراء.`)) return;
    try {
      await axios.delete(`${API}/programs/${prog.program_id}`, { headers: getAuthHeaders() });
      fetchPrograms();
      fetchStats();
      if (selectedProgramId === prog.program_id) setSelectedProgramId(null);
    } catch (e) { alert(e.response?.data?.detail || 'لا يمكن حذف البرنامج لوجود فئات مرتبطة به'); }
  };
  
  const handleToggleService = async (svc) => {
    const action = svc.is_active ? 'تعطيل' : 'تفعيل';
    if (svc.is_active && !window.confirm(`هل أنت متأكد من تعطيل الخدمة "${svc.name_ar}"؟\n\nلن تظهر في نتائج هلا.`)) return;
    try {
      await axios.patch(`${API}/services/${svc.service_id}/toggle`, {}, { headers: getAuthHeaders() });
      fetchServices();
      fetchStats();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ'); }
  };
  
  const handleDeleteService = async (svc) => {
    if (!window.confirm(`هل أنت متأكد من حذف الخدمة "${svc.name_ar}"؟\n\nلا يمكن التراجع عن هذا الإجراء.`)) return;
    try {
      await axios.delete(`${API}/services/${svc.service_id}`, { headers: getAuthHeaders() });
      fetchServices();
      fetchStats();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ'); }
  };
  
  const handleExportData = async () => {
    try {
      const format = window.confirm('اختر صيغة التصدير:\n\nOK = JSON (مستحسن للعربية)\nCancel = CSV') ? 'json' : 'csv';
      
      const response = await axios.get(`${API}/export?format=${format}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'json' ? 'application/json; charset=utf-8' : 'text/csv; charset=utf-8' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hala_services_export.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert(`تم تصدير ${services.length} خدمة بنجاح!\n\nيمكنك استخدام هذا الملف كمرجع لصيغة الاستيراد.`);
    } catch (e) {
      alert('خطأ في التصدير: ' + (e.response?.data?.detail || e.message));
    }
  };
  
  const handleWizardComplete = (returnProgramId, returnCategoryId) => {
    fetchStats();
    fetchPrograms();
    fetchCategories();
    fetchServices();
    
    // Restore context if provided (user clicked "View Services")
    if (returnProgramId) {
      setSelectedProgramId(returnProgramId);
    }
    if (returnCategoryId) {
      setSelectedCategoryId(returnCategoryId);
    }
  };
  
  if (loading) {
    return (
      <div style={{ padding: '100px', textAlign: 'center' }}>
        <div style={{ width: '48px', height: '48px', border: `3px solid ${COLORS.border}`, borderTopColor: COLORS.primary, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 20px' }} />
        <p style={{ margin: 0, fontSize: '16px', color: COLORS.textMuted }}>جاري تحميل الكتالوج...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); }}`}</style>
      </div>
    );
  }
  
  const filteredCategories = categories.filter(c => c.program_id === selectedProgramId);
  
  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '32px', fontWeight: 800, color: COLORS.text, margin: '0 0 8px' }}>إدارة الكتالوج</h1>
          <p style={{ fontSize: '15px', color: COLORS.textMuted, margin: 0 }}>
            إدارة البرامج والتصنيفات والخدمات
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button data-testid="reset-data-btn" variant="ghost" size="lg" icon={RotateCcw} onClick={() => setShowResetModal(true)}
            style={{ color: COLORS.error }}>
            إعادة تعيين
          </Button>
          <Button data-testid="export-data-btn" variant="outline" size="lg" icon={Download} onClick={handleExportData}>
            تصدير البيانات
          </Button>
          <Button data-testid="excel-import-btn" variant="primary" size="lg" icon={FileSpreadsheet} onClick={() => setShowExcelImport(true)}>
            استيراد Excel
          </Button>
          <Button variant="success" size="lg" icon={Plus} onClick={() => setShowWizard(true)}>
            إضافة خدمة
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {[
          { label: 'البرامج', value: stats.total_programs, icon: Folder, color: COLORS.primary },
          { label: 'التصنيفات', value: stats.total_categories, icon: Tag, color: COLORS.gold },
          { label: 'الخدمات', value: stats.total_services, icon: FileText, color: COLORS.success },
          { label: 'نشطة', value: stats.active_services, icon: CheckCircle, color: COLORS.success },
          { label: 'بدون توصيل', value: stats.services_without_delivery, icon: MapPin, color: stats.services_without_delivery > 0 ? COLORS.warning : COLORS.textMuted },
          { label: 'غير مرتبطة بـ AI', value: stats.services_without_ai_mapping, icon: Unlink, color: stats.services_without_ai_mapping > 0 ? COLORS.warning : COLORS.textMuted },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} style={{
              backgroundColor: COLORS.white, padding: '20px', borderRadius: '16px',
              border: `1px solid ${COLORS.border}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', backgroundColor: `${stat.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={18} color={stat.color} />
                </div>
                <span style={{ fontSize: '13px', color: COLORS.textMuted }}>{stat.label}</span>
              </div>
              <p style={{ fontSize: '32px', fontWeight: 800, color: stat.color, margin: 0 }}>{stat.value || 0}</p>
            </div>
          );
        })}
      </div>
      
      {/* Programs Section */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Folder size={22} color={COLORS.primary} />
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: COLORS.text, margin: 0 }}>البرامج</h2>
            <Badge color={COLORS.info} icon={Info}>للتنظيم فقط</Badge>
          </div>
          <Button variant="outline" size="sm" icon={Plus} onClick={() => { setEditingProgram(null); setShowProgramModal(true); }}>
            برنامج جديد
          </Button>
        </div>
        
        {programs.length === 0 ? (
          <EmptyState
            icon={Folder}
            title="لا توجد برامج"
            description="قم بإنشاء برنامج أولاً لتنظيم الخدمات"
            action={
              <Button variant="primary" icon={Plus} onClick={() => { setEditingProgram(null); setShowProgramModal(true); }}>
                إنشاء برنامج
              </Button>
            }
          />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
            {programs.map(prog => (
              <ProgramCard
                key={prog.program_id}
                program={prog}
                isSelected={selectedProgramId === prog.program_id}
                onClick={() => { setSelectedProgramId(prog.program_id); setSelectedCategoryId(null); }}
                onEdit={() => { setEditingProgram(prog); setShowProgramModal(true); }}
                onToggle={() => handleToggleProgram(prog)}
                onDelete={() => handleDeleteProgram(prog)}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Categories Section */}
      {selectedProgramId && (
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <Tag size={22} color={COLORS.gold} />
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: COLORS.text, margin: 0 }}>التصنيفات</h2>
            <Badge color={COLORS.warning} icon={AlertTriangle}>يستخدمه AI</Badge>
          </div>
          
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setSelectedCategoryId(null)}
              style={{
                padding: '10px 20px', borderRadius: '25px', fontSize: '14px', fontWeight: 500,
                border: `2px solid ${!selectedCategoryId ? COLORS.primary : COLORS.border}`,
                backgroundColor: !selectedCategoryId ? `${COLORS.primary}10` : COLORS.white,
                color: !selectedCategoryId ? COLORS.primary : COLORS.textMuted,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              الكل ({services.length})
            </button>
            {filteredCategories.map(cat => {
              const typeConfig = CATEGORY_TYPES.find(t => t.value === cat.category_type);
              return (
                <button
                  key={cat.category_id}
                  onClick={() => setSelectedCategoryId(cat.category_id)}
                  style={{
                    padding: '10px 20px', borderRadius: '25px', fontSize: '14px', fontWeight: 500,
                    border: `2px solid ${selectedCategoryId === cat.category_id ? typeConfig?.color || COLORS.gold : COLORS.border}`,
                    backgroundColor: selectedCategoryId === cat.category_id ? `${typeConfig?.color || COLORS.gold}10` : COLORS.white,
                    color: selectedCategoryId === cat.category_id ? typeConfig?.color || COLORS.gold : COLORS.textMuted,
                    cursor: 'pointer', transition: 'all 0.2s',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}
                >
                  {cat.name_ar}
                  <span style={{
                    padding: '2px 8px', borderRadius: '10px', fontSize: '12px',
                    backgroundColor: selectedCategoryId === cat.category_id ? typeConfig?.color || COLORS.gold : COLORS.bgDark,
                    color: selectedCategoryId === cat.category_id ? COLORS.white : COLORS.textMuted,
                  }}>{cat.services_count || 0}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Services Section */}
      <div style={{ backgroundColor: COLORS.white, borderRadius: '20px', border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FileText size={22} color={COLORS.success} />
            <h2 style={{ fontSize: '20px', fontWeight: 700, color: COLORS.text, margin: 0 }}>الخدمات</h2>
            <Badge color={COLORS.warning} icon={AlertTriangle}>يستخدمه AI</Badge>
          </div>
          
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            backgroundColor: COLORS.bg, padding: '10px 16px', borderRadius: '12px', width: '320px',
          }}>
            <Search size={18} color={COLORS.textMuted} />
            <input
              type="text"
              placeholder="بحث في الخدمات..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                border: 'none', backgroundColor: 'transparent', outline: 'none',
                flex: 1, fontSize: '14px', fontFamily: 'inherit',
              }}
            />
          </div>
        </div>
        
        <ServiceTable
          services={services}
          loading={servicesLoading}
          onEdit={(svc) => { setEditingService(svc); setShowWizard(true); }}
          onToggle={handleToggleService}
          onDelete={handleDeleteService}
          onView={(svc) => { setEditingService(svc); setShowWizard(true); }}
        />
      </div>
      
      {/* Modals */}
      <EditProgramModal
        program={editingProgram}
        isOpen={showProgramModal}
        onClose={() => { setShowProgramModal(false); setEditingProgram(null); }}
        onSave={handleSaveProgram}
      />
      
      <ServiceWizard
        isOpen={showWizard}
        onClose={() => { setShowWizard(false); setEditingService(null); }}
        onComplete={handleWizardComplete}
        programs={programs}
        categories={categories}
        contextProgramId={selectedProgramId}
        contextCategoryId={selectedCategoryId}
        editingService={editingService}
      />
      
      <BulkImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={(count) => {
          fetchStats();
          fetchPrograms();
          fetchCategories();
          fetchServices();
        }}
      />
      
      <DataResetModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onResetComplete={() => {
          fetchStats();
          fetchPrograms();
          fetchCategories();
          fetchServices();
        }}
      />
      
      {/* Excel Import Modal */}
      <ExcelImportModal
        isOpen={showExcelImport}
        onClose={() => setShowExcelImport(false)}
        onImportComplete={() => {
          fetchStats();
          fetchPrograms();
          fetchCategories();
          fetchServices();
        }}
      />
    </div>
  );
}
